import { Bullet, Label, color, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  StepLineSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { forIn } from "lodash";

import BaseChart from "@/classes/BaseChart";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

const KFF_FOR_MAX = 0.9; // Of the axes
const KFF_FOR_TARGET_DEFAULT = 0.9; // Of the metric
const KFF_QUALITATIVE_RANGE_DEFAULT = 0.6; // Of the axes
const KFF_QUALITATIVE_RANGE_DEFAULT2 = 0.8; // Of the axes
const KFF_QUALITATIVE_RANGE_DEFAULT3 = 1; // Of the axes

export default class BulletChart extends BaseChart {
  metric!: number;
  maxQualitative!: number;
  qualitativeRanges!: number[];
  isPercent!: boolean;
  isDefault!: boolean;
  declare renderers: { x: AxisRendererX; y: AxisRendererY };
  declare chart: XYChart;
  declare axes: {
    x: ValueAxis<AxisRenderer>;
    y: CategoryAxis<AxisRenderer>;
  };

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      this.chart.series.clear();
    }

    this.updateQualitativeRanges();
    this.createChart();
    this.createRenderers();
    this.createAxes();
    this.createSeries();
    this.updateQualitativeRanges(true);
    this.createRange();
    this.createCursor();
  }

  createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: false,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        arrangeTooltips: false,
      })
    );
  }

  createRenderers() {
    const x = AxisRendererX.new(this.root, {});
    x.grid.template.set("forceHidden", true);
    x.labels.template.setAll({
      centerX: p50,
      ...chartFontSettings.label,
    });

    const y = AxisRendererY.new(this.root, {});
    const text =
      this.metricsDictionary.find((el) => {
        return el.value === this.settings.yAxis;
      })?.title || this.settings.yAxis;
    y.labels.template.setAll({ ...chartFontSettings.label, text });

    this.renderers = { x, y };
  }

  createCursor() {
    const cursor = this.chart.set(
      "cursor",
      XYCursor.new(this.root, {
        behavior: "zoomX",
      })
    );
    cursor.lineY.set("visible", false);
  }

  createAxes() {
    const maxValue = this.maxQualitative > this.metric ? this.maxQualitative : this.metric / KFF_FOR_MAX;

    const x = this.chart.xAxes.push(
      ValueAxis.new(this.root, {
        renderer: this.renderers.x,
        marginTop: 14,
        min: 0,
        max: maxValue,
        strictMinMax: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.yAxis, this.data),
      })
    );

    const y = this.chart.yAxes.push(
      CategoryAxis.new(this.root, {
        renderer: this.renderers.y,
        marginRight: 14,
        categoryField: this.settings.yAxis,
      })
    );

    y.data.setAll(this.data);

    if (this.settings.yAxisTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, y };
  }

  createSeries() {
    const series = this.chart.series.push(
      ColumnSeries.new(this.root, {
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        valueXField: "value",
        categoryYField: this.settings.yAxis,
        fill: color(this.themeColors["divider-dark"] as string),
        stroke: color(this.themeColors["divider-dark"] as string),
      })
    );

    if (this.settings.showDataLabels) {
      const metricFormatter = this.formatters?.[this.settings.yAxis];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerY: p50,
          dx: 8,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueX");
          if (typeof value !== "number") {
            return "";
          }

          if (metricFormatter) {
            return metricFormatter(value, formatterConfig);
          }

          return this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const v = dataItem?.get("valueX");
          return typeof v === "number" ? v !== 0 : !!v;
        });

        return Bullet.new(this.root, {
          locationX: 1,
          sprite: label,
        });
      });
    }

    series.columns.template.setAll({
      height: percent(40),
    });

    series.data.setAll(this.data);

    if (this.settings.targetValue !== null) {
      const stepSeries = this.chart.series.push(
        StepLineSeries.new(this.root, {
          xAxis: this.axes.x,
          yAxis: this.axes.y,
          valueXField: "targetValue",
          categoryYField: this.settings.yAxis,
          stroke: color(this.themeColors["accent-global"] as string),
          fill: color(this.themeColors["accent-global"] as string),
          noRisers: true,
          stepWidth: percent(70),
        })
      );

      stepSeries.strokes.template.set("strokeWidth", 3);
      stepSeries.data.setAll(this.data);
    }

    this.createTooltip(series as ColumnSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          if (series.get("categoryYField") === key) {
            param.push({ key, value: tooltipValues.value });
            param.push({ key: "targetValue", value: tooltipValues.targetValue });
          }
        });
      }
      return param;
    });
  }

  hexToRgb(hex: string): { r: number; g: number; b: number } {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  }

  createRange() {
    const colors = this.getColors();

    this.qualitativeRanges.reduce((prev, next, index) => {
      const rangeDataItem = this.axes.x.makeDataItem({
        value: prev,
        endValue: next,
      });
      this.axes.x.createAxisRange(rangeDataItem);
      rangeDataItem.get("axisFill")?.setAll({
        visible: true,
        fill: color(colors[index]),
        stroke: color(colors[index]),
        fillOpacity: 1,
      });
      return next;
    }, 0);
  }

  updateQualitativeRanges(useDefault?: boolean): void {
    const xAxesMax = useDefault ? this.axes.x.get("max") || 0 : 0;
    this.qualitativeRanges = [
      useDefault && this.isDefault ? xAxesMax * KFF_QUALITATIVE_RANGE_DEFAULT : this.settings.qualitativeRange,
      useDefault && this.isDefault ? xAxesMax * KFF_QUALITATIVE_RANGE_DEFAULT2 : this.settings.qualitativeRange2,
      useDefault && this.isDefault ? xAxesMax * KFF_QUALITATIVE_RANGE_DEFAULT3 : this.settings.qualitativeRange3,
      useDefault && this.settings.qualitativeRange4,
      useDefault && this.settings.qualitativeRange5,
    ]
      .filter((el) => !!el)
      .map((el) => {
        return this.isPercent ? Number(el) / 100 : Number(el);
      });
    this.maxQualitative = Math.max(...this.qualitativeRanges) || 0;
  }

  getColors = (): string[] => {
    const colors: string[] = [];
    const minQualitative = Math.min(...this.qualitativeRanges) || 0;
    const qualitativePercent = (this.maxQualitative - minQualitative) / 100;

    const minColor = this.hexToRgb(this.settings.minValueColor);
    const maxColor = this.hexToRgb(this.settings.maxValueColor);
    const getColorChannel = (qualitative: number, channel: "r" | "g" | "b") => {
      return (
        minColor[channel] +
        Math.round(
          ((qualitative - minQualitative) / qualitativePercent) * ((maxColor[channel] - minColor[channel]) / 100)
        )
      ).toString(16);
    };
    this.qualitativeRanges.forEach((el) => {
      const color = {
        r: getColorChannel(el, "r"),
        g: getColorChannel(el, "g"),
        b: getColorChannel(el, "b"),
      };

      colors.push(`#${Object.values(color).join("")}`);
    });
    return colors;
  };

  mapData(data: ChartDataRecord[]) {
    this.isPercent = this.formatters[this.settings.yAxis]?.name === "percentFormat";
    this.isDefault = this.settings.qualitativeRange === null;
    const metric = data.find((el) => {
      return !!el[this.settings.yAxis];
    });

    if (!metric) {
      return [];
    }

    this.metric = Number(metric[this.settings.yAxis]) || 0;
    const targetValue = this.isDefault ? this.metric * KFF_FOR_TARGET_DEFAULT : this.settings.targetValue;

    return [
      {
        [this.settings.yAxis]: this.settings.yAxis,
        value: metric[this.settings.yAxis] as number,
        targetValue: this.isPercent ? Number(targetValue) / 100 : Number(targetValue),
      },
    ];
  }
}
